import React, { useState } from 'react';
import { useKioskStore } from '@/store/kiosk-store';
import { ApiClient, AuthError, NotFoundError, HttpError } from '@/lib/api-client';
import { normalizeProvisionCode } from '@museum-kiosk/shared-types';
import type { ProvisionResponse } from '@museum-kiosk/shared-types';

const client = new ApiClient();

/** Tell the person at the kiosk what actually went wrong, not just "network". */
function provisioningErrorMessage(err: unknown): string {
  if (err instanceof AuthError) return 'Invalid provisioning code. Please try again.';
  if (err instanceof NotFoundError) {
    return 'Provisioning code not found. Check the code and try again.';
  }
  if (err instanceof HttpError && (err.status === 409 || err.status === 410)) {
    return 'This provisioning code was already used or has expired. Generate a new code in Kiosk Admin.';
  }
  if (err instanceof HttpError) {
    return 'The server could not provision this kiosk. Please try again or contact support.';
  }
  return 'Unable to connect. Please check the network connection.';
}

const ProvisioningScreen: React.FC = () => {
  const setProvisioned = useKioskStore((s) => s.setProvisioned);

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedCode = normalizeProvisionCode(code);
    if (!normalizedCode) return;

    setError(null);
    setIsLoading(true);

    try {
      const data = await client.post<ProvisionResponse>('/api/auth/provision', { code: normalizedCode });

      if (data.provisioned && data.token) {
        // Store device JWT directly (1-year expiry). No cookie needed.
        const expiresAt = Date.now() + 365 * 24 * 3600 * 1000;
        useKioskStore.getState().setAuth(data.token, expiresAt);
        setProvisioned();
      } else {
        setError('Provisioning failed. Please check the code and try again.');
      }
    } catch (err) {
      setError(provisioningErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-screen w-screen bg-gray-50">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-primary mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-8 h-8 text-white"
              aria-hidden="true"
            >
              <path d="M2 20h20" />
              <path d="M5 20V8l7-5 7 5v12" />
              <path d="M9 20v-6h6v6" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Kiosk Setup</h2>
          <p className="text-sm text-gray-500 mt-1.5">
            Enter the provisioning code provided by your administrator.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
          <div className="space-y-1.5">
            <label htmlFor="provision-code" className="text-sm font-medium text-gray-700">
              Provisioning Code
            </label>
            <input
              id="provision-code"
              type="text"
              placeholder="e.g. KIOSK-A1B2C3"
              value={code}
              // Keep what was typed: rewriting a controlled value per keystroke moves
              // the caret and breaks IME composition on some Android keyboards.
              // The CSS `uppercase` class shows it as it will be sent (normalized on submit).
              onChange={(e) => setCode(e.target.value)}
              disabled={isLoading}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="w-full h-14 rounded-xl border border-gray-300 bg-white px-4 text-center text-lg font-mono tracking-widest uppercase placeholder:text-gray-400 placeholder:font-sans placeholder:tracking-normal placeholder:text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 text-center" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading || !code.trim()}
            className="w-full h-14 rounded-xl bg-primary text-white text-base font-medium transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
          >
            {isLoading ? 'Connecting...' : 'Provision Kiosk'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProvisioningScreen;
